import styled, { css } from 'styled-components';

export const Container = styled.div`
  position: relative;
  width: ${(props) => props.$width || '300px'};
  user-select: none;
`;

export const SelectInput = styled.div.attrs({ role: 'combobox', tabIndex: 0 })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.$isOpen ? 'var(--cp-accent, #007bff)' : '#ccc')};
  border-radius: 6px;
  background-color: #fff;
  cursor: pointer;
  box-sizing: border-box;
  transition: all 0.2s ease-in-out;
  box-shadow: ${(props) => (props.$isOpen ? '0 0 0 2px rgba(0, 123, 255, 0.25)' : 'none')};

  &:focus-visible {
    outline: 2px solid var(--cp-accent, #007bff);
    outline-offset: 1px;
  }
  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      background-color: #4f5761;
      border-color: ${(props) => (props.$isOpen ? 'var(--cp-accent, #007bff)' : '#767676')};
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      background-color: var(--cp-surface);
      color: var(--cp-text);
      border-color: ${(props) => (props.$isOpen ? 'var(--cp-accent, #007bff)' : 'var(--cp-border)')};
      box-shadow: ${(props) => (props.$isOpen ? '0 0 0 3px rgba(71, 185, 114, 0.15)' : 'none')};
      /* Unified CP focus ring — same green border + 3px/0.15 ring the text
         filters and CustomSelect use, instead of the default 2px solid outline
         (which read as a different highlight from every other filter). */
      &:focus-visible {
        outline: none;
        border-color: var(--cp-accent, #47b972);
        box-shadow: 0 0 0 3px rgba(71, 185, 114, 0.15);
      }
    `}
`;

export const SelectText = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 8px;
  color: ${(props) => (props.$isPlaceholder ? '#999' : '#333')};
  font-size: 14px;
  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      color: #fff;
    `}
`;

export const Arrow = styled.span`
  border: solid #666;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  transform: ${(props) => (props.$isOpen ? 'rotate(-135deg)' : 'rotate(45deg)')};
  transition: transform 0.2s ease-in-out;
  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      border-color: #fff;
    `}
`;

export const Dropdown = styled.div.attrs({ role: 'listbox' })`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      background-color: #4f5761;
      border-color: #767676;
    `}

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      /* Match the single-select CP dropdown (CustomSelect / CsDropdown): rounded
         card, surface + border from tokens, soft shadow, 4px inner padding. */
      background-color: var(--cp-surface);
      color: var(--cp-text);
      border-color: var(--cp-border);
      border-radius: 10px;
      padding: 4px;
      box-shadow: 0 8px 24px rgba(16, 24, 32, 0.16);
      z-index: 3000;
    `}
`;

export const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 12px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f8f9fa;
    ${(props) =>
      props.theme.theme === 'dark' &&
      css`
        background-color: #767676;
      `}
    ${(props) =>
      props.theme.ui === 'v2' &&
      css`
        background-color: var(--cp-surface-hover);
      `}
  }

  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      border-radius: 6px;
      padding: 7px 10px;
    `}

  /* Selected row — brand-green tint + green label, identical to the single
     ZoneSelect's selected option (CsOption). Purely visual; the checkbox stays
     the source of truth for the multi-select value. */
  ${(props) =>
    props.$selected &&
    props.theme.ui === 'v2' &&
    css`
      background-color: rgba(71, 185, 114, 0.12);
      &:hover {
        background-color: rgba(71, 185, 114, 0.16);
      }
    `}
`;

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
  width: 16px !important;
  height: 16px !important;
  accent-color: var(--cp-accent, #007bff);
`;

export const LabelText = styled.span`
  font-size: 14px;
  color: #333;
  ${(props) =>
    props.theme.theme === 'dark' &&
    css`
      color: #eee;
    `}
  ${(props) =>
    props.theme.ui === 'v2' &&
    css`
      color: var(--cp-text);
    `}
  /* selected → green + semibold, matching the single ZoneSelect (CsOption) */
  ${(props) =>
    props.$selected &&
    props.theme.ui === 'v2' &&
    css`
      color: var(--cp-green, #47b972);
      font-weight: 600;
    `}
`;
